import { HttpErrorResponse } from '@angular/common/http';
import { isGeometryProcessKey } from '@app/pages/admin/orchestration-process-keys.util';
import { parseProcessParametersJson } from '@app/pages/admin/process-parameters-json.util';
import {
  DatePlaceholder,
  DatePlaceholderSign,
  DatePlaceholderUnit,
  formatDatePlaceholder,
  isSubscriptionDateToken,
  parseDatePlaceholder,
  parseDatePlaceholderAmount,
  validateSubscriptionDatePlaceholders,
} from '@app/pages/admin/process-subscription-date.util';
import {
  defaultParameterFieldValues,
  parameterFieldSchema,
  parameterFieldValuesFromSource,
} from '@app/pages/admin/assignment-parameter-fields.util';
import {
  LOCALE_DEFAULT_END_ISO,
  LOCALE_DEFAULT_START_ISO,
  parseFlexibleDateToIso,
} from '@app/utils/locale-date.util';

export type AssignmentStartKind =
  | 'catalogue'
  | 'post-processing'
  | 'farmer-reports'
  | 'bid-templates'
  | 'catch-up'
  | 'probe'
  | 'season-indices'
  | 'fields'
  | 'json';

export interface AssignmentStartSelectOption {
  id: string;
  label: string;
}

export interface AssignmentStartFormValue {
  name: string;
  geometrySource: string;
  regionIds: string[];
  countryName: string;
  geoJson: string;
  geoJsonFileId: string;
  startDate: Date | string | null;
  endDate: Date | string | null;
  sentinel: boolean;
  landsat: boolean;
  priority: string;
  maximumCloudCover: number | null;
  satelliteProductId: string;
  maxReportsPerRun: number;
  alsoPdf: boolean;
  locale: string;
  bucket: string;
  catchUpTake: number;
  failEvery: number;
  fieldId: string;
  forceSeasonIndices: boolean;
  polygonDetectionJobId: string;
  parametersJson: string;
  parameterFields: Record<string, string>;
}

export type AssignmentStartBuildResult =
  | { ok: true; parameters: Record<string, unknown> }
  | { ok: false; error: string };

export const GEOMETRY_SOURCE_OPTIONS: AssignmentStartSelectOption[] = [
  { id: 'CountryBoundaries', label: 'Configured country boundary' },
  { id: 'CountryName', label: 'Country name' },
  { id: 'Regions', label: 'Regions' },
  { id: 'GeoJson', label: 'Polygon (GeoJSON)' },
  { id: 'GeoJsonFile', label: 'GeoJSON file' },
];

export const PRIORITY_OPTIONS: AssignmentStartSelectOption[] = [
  { id: 'Low', label: 'Low' },
  { id: 'Normal', label: 'Normal' },
  { id: 'High', label: 'High' },
  { id: 'Critical', label: 'Critical' },
];

export const ASSIGNMENT_DATE_MODE_CALENDAR = 'calendar';
export const ASSIGNMENT_DATE_MODE_TODAY = 'today';
export const ASSIGNMENT_DATE_MODE_NOW = 'now';

export interface AssignmentDateFieldState {
  mode: string;
  showOffset: boolean;
  sign: DatePlaceholderSign;
  amount: number;
  unit: DatePlaceholderUnit;
}

export const ASSIGNMENT_DATE_PLACEHOLDER_OPTIONS: AssignmentStartSelectOption[] = [
  { id: ASSIGNMENT_DATE_MODE_CALENDAR, label: 'Calendar date' },
  { id: ASSIGNMENT_DATE_MODE_TODAY, label: '{{today}}' },
  { id: ASSIGNMENT_DATE_MODE_NOW, label: '{{now}}' },
];

export const ASSIGNMENT_DATE_SIGN_OPTIONS: AssignmentStartSelectOption[] = [
  { id: '-', label: '−' },
  { id: '+', label: '+' },
];

export const ASSIGNMENT_DATE_UNIT_OPTIONS: AssignmentStartSelectOption[] = [
  { id: 'h', label: 'Date offset hours' },
  { id: 'd', label: 'Date offset days' },
  { id: 'm', label: 'Date offset months' },
];

export function assignmentDateMode(value: Date | string | null): string {
  return parseDatePlaceholder(typeof value === 'string' ? value : null)?.base
    ?? ASSIGNMENT_DATE_MODE_CALENDAR;
}

export function assignmentDateFieldState(value: Date | string | null): AssignmentDateFieldState {
  const parsed = typeof value === 'string' ? parseDatePlaceholder(value) : null;
  return {
    mode: parsed?.base ?? ASSIGNMENT_DATE_MODE_CALENDAR,
    showOffset: parsed !== null,
    sign: parsed?.sign ?? '-',
    amount: parsed?.amount ?? 0,
    unit: parsed?.unit ?? 'd',
  };
}

export function assignmentDateFromMode(
  mode: string | null | undefined,
  calendarIso = LOCALE_DEFAULT_START_ISO,
  current?: Date | string | null,
): Date | string {
  const selected = (mode ?? '').trim();
  const selectedPlaceholder = parseDatePlaceholder(selected);
  const base = selected === ASSIGNMENT_DATE_MODE_NOW || selectedPlaceholder?.base === 'now'
    ? 'now'
    : selected === ASSIGNMENT_DATE_MODE_TODAY || selectedPlaceholder?.base === 'today'
      ? 'today'
      : null;
  if (!base) {
    return dateFromIso(calendarIso);
  }
  const currentPlaceholder = typeof current === 'string' ? parseDatePlaceholder(current) : null;
  return formatDatePlaceholder({
    base,
    sign: currentPlaceholder?.sign ?? selectedPlaceholder?.sign ?? '-',
    amount: currentPlaceholder?.amount ?? selectedPlaceholder?.amount ?? 0,
    unit: currentPlaceholder?.unit ?? selectedPlaceholder?.unit ?? 'd',
  });
}

export function assignmentDateFromOffset(
  current: Date | string | null,
  patch: Partial<DatePlaceholder>,
): string {
  const parsed = typeof current === 'string' ? parseDatePlaceholder(current) : null;
  return formatDatePlaceholder({
    base: patch.base ?? parsed?.base ?? 'today',
    sign: patch.sign ?? parsed?.sign ?? '-',
    amount: parseDatePlaceholderAmount(patch.amount !== undefined ? patch.amount : parsed?.amount ?? 0),
    unit: patch.unit ?? parsed?.unit ?? 'd',
  });
}

export const CATCH_UP_POSTPROCESSING_PROCESS_KEY = 'satellite.catch-up-product-postprocessing';
export const CATCH_UP_DEFAULT_TAKE = 50;
export const CATCH_UP_MAX_TAKE = 500;

export function isCatchUpPostProcessingProcess(processKey?: string | null): boolean {
  return processKey === CATCH_UP_POSTPROCESSING_PROCESS_KEY;
}

export function normalizeCatchUpTake(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const rounded = Math.trunc(parsed);
  if (rounded === 0) {
    return CATCH_UP_DEFAULT_TAKE;
  }
  if (rounded < 1 || rounded > CATCH_UP_MAX_TAKE) {
    return null;
  }
  return rounded;
}

export function catchUpTakeFromUnknown(source?: Record<string, unknown> | null): number {
  return normalizeCatchUpTake(source?.['take'] ?? source?.['Take']) ?? CATCH_UP_DEFAULT_TAKE;
}

export function catchUpTakeFromJson(json?: string | null): number {
  if (!json || !json.trim()) {
    return CATCH_UP_DEFAULT_TAKE;
  }
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return CATCH_UP_DEFAULT_TAKE;
    }
    return catchUpTakeFromUnknown(parsed as Record<string, unknown>);
  } catch {
    return CATCH_UP_DEFAULT_TAKE;
  }
}

export function buildCatchUpParameters(take: number): Record<string, unknown> {
  return { take };
}

export const PROBE_PROCESS_KEY = 'orchestration.probe';
export const PROBE_DEFAULT_FAIL_EVERY = 2;

export function isOrchestrationProbeProcess(processKey?: string | null): boolean {
  return processKey === PROBE_PROCESS_KEY;
}

export function processesVisibleOnAssignmentsList<T extends { key?: string | null }>(
  processes: T[],
): T[] {
  return processes.filter(process =>
    !isOrchestrationProbeProcess(process.key) && !isGeometryProcessKey(process.key));
}

export function normalizeProbeFailEvery(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const rounded = Math.trunc(parsed);
  if (rounded === 0) {
    return PROBE_DEFAULT_FAIL_EVERY;
  }
  if (rounded < 1) {
    return null;
  }
  return rounded;
}

export function probeFailEveryFromUnknown(source?: Record<string, unknown> | null): number {
  return normalizeProbeFailEvery(source?.['failEvery'] ?? source?.['FailEvery']) ?? PROBE_DEFAULT_FAIL_EVERY;
}

export function probeFailEveryFromJson(json?: string | null): number {
  if (!json || !json.trim()) {
    return PROBE_DEFAULT_FAIL_EVERY;
  }
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return PROBE_DEFAULT_FAIL_EVERY;
    }
    return probeFailEveryFromUnknown(parsed as Record<string, unknown>);
  } catch {
    return PROBE_DEFAULT_FAIL_EVERY;
  }
}

export function buildProbeParameters(failEvery: number): Record<string, unknown> {
  return { failEvery };
}

export const FARMER_REPORTS_PROCESS_KEY = 'analitic.generate-farmer-reports';
export const FARMER_REPORTS_DEFAULT_MAX = 30;
export const FARMER_REPORTS_MAX_ALLOWED = 100;

export function isFarmerReportsProcess(processKey?: string | null): boolean {
  return processKey === FARMER_REPORTS_PROCESS_KEY;
}

export function normalizeFarmerReportsMax(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const rounded = Math.trunc(parsed);
  if (rounded === 0) {
    return FARMER_REPORTS_DEFAULT_MAX;
  }
  if (rounded < 1 || rounded > FARMER_REPORTS_MAX_ALLOWED) {
    return null;
  }
  return rounded;
}

export function farmerReportsParametersFromUnknown(
  source?: Record<string, unknown> | null,
): { maxReportsPerRun: number; alsoPdf: boolean } {
  const max = normalizeFarmerReportsMax(
    source?.['maxReportsPerRun'] ?? source?.['MaxReportsPerRun'],
  );
  const alsoPdf = source?.['alsoPdf'] ?? source?.['AlsoPdf'];
  return {
    maxReportsPerRun: max ?? FARMER_REPORTS_DEFAULT_MAX,
    alsoPdf: alsoPdf === true,
  };
}

export function farmerReportsFormFromJson(json?: string | null): { maxReportsPerRun: number; alsoPdf: boolean } {
  if (!json || !json.trim()) {
    return farmerReportsParametersFromUnknown(null);
  }
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return farmerReportsParametersFromUnknown(null);
    }
    return farmerReportsParametersFromUnknown(parsed as Record<string, unknown>);
  } catch {
    return farmerReportsParametersFromUnknown(null);
  }
}

export function buildFarmerReportsParameters(
  maxReportsPerRun: number,
  alsoPdf: boolean,
): Record<string, unknown> {
  return { maxReportsPerRun, alsoPdf };
}

export const BID_TEMPLATES_PROCESS_KEY = 'crop.export-bid-templates';
export const BID_TEMPLATES_DEFAULT_LOCALE = 'ru-RU';
export const BID_TEMPLATES_DEFAULT_BUCKET = 'bid-template-exports';
export const BID_TEMPLATES_DEFAULT_FROM = '{{today-1d}}';
export const BID_TEMPLATES_DEFAULT_TO = '{{today}}';

export function isBidTemplatesProcess(processKey?: string | null): boolean {
  return processKey === BID_TEMPLATES_PROCESS_KEY;
}

export function bidTemplatesParametersFromUnknown(
  source?: Record<string, unknown> | null,
): { fromUtc: string; toUtc: string; locale: string; bucket: string } {
  const fromUtc = readStringField(source, 'fromUtc', 'FromUtc') ?? BID_TEMPLATES_DEFAULT_FROM;
  const toUtc = readStringField(source, 'toUtc', 'ToUtc') ?? BID_TEMPLATES_DEFAULT_TO;
  const locale = readStringField(source, 'locale', 'Locale') ?? BID_TEMPLATES_DEFAULT_LOCALE;
  const bucket = readStringField(source, 'bucket', 'Bucket') ?? BID_TEMPLATES_DEFAULT_BUCKET;
  return { fromUtc, toUtc, locale, bucket };
}

export function bidTemplatesFormFromJson(
  json?: string | null,
): { fromUtc: string; toUtc: string; locale: string; bucket: string } {
  if (!json || !json.trim()) {
    return bidTemplatesParametersFromUnknown(null);
  }
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return bidTemplatesParametersFromUnknown(null);
    }
    return bidTemplatesParametersFromUnknown(parsed as Record<string, unknown>);
  } catch {
    return bidTemplatesParametersFromUnknown(null);
  }
}

export function buildBidTemplatesParameters(
  fromUtc: string,
  toUtc: string,
  locale: string,
  bucket: string,
): Record<string, unknown> {
  return {
    fromUtc,
    toUtc,
    locale: locale.trim() || BID_TEMPLATES_DEFAULT_LOCALE,
    bucket: bucket.trim() || BID_TEMPLATES_DEFAULT_BUCKET,
  };
}

function readStringField(
  source: Record<string, unknown> | null | undefined,
  ...names: string[]
): string | null {
  if (!source) {
    return null;
  }
  for (const name of names) {
    const value = source[name];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString();
    }
  }
  return null;
}

export const SATELLITE_MEASURE_DOWNLOAD_BACKLOG_KEY = 'satellite.measure-download-backlog';

export function isSatelliteDownloadSelectionProcess(processKey?: string | null): boolean {
  return processKey === 'satellite.download-found-products'
    || processKey === SATELLITE_MEASURE_DOWNLOAD_BACKLOG_KEY;
}

export function isSatelliteCatalogueProcess(processKey?: string | null): boolean {
  return processKey === 'satellite.find-products'
    || isSatelliteDownloadSelectionProcess(processKey);
}

export function assignmentHasSatelliteProducts(processKey?: string | null): boolean {
  return processKey === 'satellite.find-products'
    || processKey === 'satellite.download-found-products'
    || processKey === 'satellite.download-product'
    || processKey === 'satellite.create-product-postprocessing';
}

export const CALCULATE_SEASON_INDICES_PROCESS_KEY = 'satellite.calculate-season-indices';
export const BACKFILL_SEASON_INDICES_PROCESS_KEY = 'satellite.backfill-season-indices';
const GUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export function isCalculateSeasonIndicesProcess(processKey?: string | null): boolean {
  return processKey === CALCULATE_SEASON_INDICES_PROCESS_KEY;
}

export function isBackfillSeasonIndicesProcess(processKey?: string | null): boolean {
  return processKey === BACKFILL_SEASON_INDICES_PROCESS_KEY;
}

export function isSeasonIndicesProcess(processKey?: string | null): boolean {
  return isCalculateSeasonIndicesProcess(processKey) || isBackfillSeasonIndicesProcess(processKey);
}

export function assignmentStartKind(processKey: string): AssignmentStartKind {
  if (isSatelliteCatalogueProcess(processKey)) {
    return 'catalogue';
  }
  if (processKey === 'satellite.create-product-postprocessing'
    || processKey === 'satellite.download-product') {
    return 'post-processing';
  }
  if (isFarmerReportsProcess(processKey)) {
    return 'farmer-reports';
  }
  if (isBidTemplatesProcess(processKey)) {
    return 'bid-templates';
  }
  if (isCatchUpPostProcessingProcess(processKey)) {
    return 'catch-up';
  }
  if (isOrchestrationProbeProcess(processKey)) {
    return 'probe';
  }
  if (isSeasonIndicesProcess(processKey)) {
    return 'season-indices';
  }
  if (parameterFieldSchema(processKey)) {
    return 'fields';
  }
  return 'json';
}

function assignmentStartDefaultDate(
  processKey: string,
  bidTemplatesDefault: string,
  localeDefaultIso: string,
): Date | string | null {
  if (isBidTemplatesProcess(processKey)) {
    return bidTemplatesDefault;
  }
  if (isSeasonIndicesProcess(processKey)) {
    return null;
  }
  return dateFromIso(localeDefaultIso);
}

export function defaultAssignmentStartForm(processKey: string): AssignmentStartFormValue {
  return {
    name: 'Satellite products',
    geometrySource: 'CountryBoundaries',
    regionIds: [],
    countryName: '',
    geoJson: '',
    geoJsonFileId: '',
    startDate: assignmentStartDefaultDate(
      processKey,
      BID_TEMPLATES_DEFAULT_FROM,
      LOCALE_DEFAULT_START_ISO,
    ),
    endDate: assignmentStartDefaultDate(
      processKey,
      BID_TEMPLATES_DEFAULT_TO,
      LOCALE_DEFAULT_END_ISO,
    ),
    sentinel: true,
    landsat: true,
    priority: 'Normal',
    maximumCloudCover: null,
    satelliteProductId: '',
    maxReportsPerRun: FARMER_REPORTS_DEFAULT_MAX,
    alsoPdf: false,
    locale: BID_TEMPLATES_DEFAULT_LOCALE,
    bucket: BID_TEMPLATES_DEFAULT_BUCKET,
    catchUpTake: CATCH_UP_DEFAULT_TAKE,
    failEvery: PROBE_DEFAULT_FAIL_EVERY,
    fieldId: '',
    forceSeasonIndices: false,
    polygonDetectionJobId: '',
    parametersJson: defaultParametersJson(processKey),
    parameterFields: defaultParameterFieldValues(parameterFieldSchema(processKey) ?? []),
  };
}

export function assignmentStartFormFromParameters(
  processKey: string,
  source?: Record<string, unknown> | null,
): AssignmentStartFormValue {
  const defaults = defaultAssignmentStartForm(processKey);
  if (!source) {
    return defaults;
  }
  const kind = assignmentStartKind(processKey);
  if (kind === 'farmer-reports') {
    return { ...defaults, ...farmerReportsParametersFromUnknown(source) };
  }
  if (kind === 'bid-templates') {
    const bidTemplates = bidTemplatesParametersFromUnknown(source);
    return {
      ...defaults,
      startDate: bidTemplates.fromUtc,
      endDate: bidTemplates.toUtc,
      locale: bidTemplates.locale,
      bucket: bidTemplates.bucket,
    };
  }
  if (kind === 'catch-up') {
    return { ...defaults, catchUpTake: catchUpTakeFromUnknown(source) };
  }
  if (kind === 'probe') {
    return { ...defaults, failEvery: probeFailEveryFromUnknown(source) };
  }
  if (kind === 'season-indices') {
    const fieldId = firstString(source, 'fieldId', 'FieldId') ?? '';
    const polygonDetectionJobId = firstString(source, 'polygonDetectionJobId', 'PolygonDetectionJobId') ?? '';
    const force = source['force'] ?? source['Force'];
    return {
      ...defaults,
      fieldId,
      polygonDetectionJobId,
      forceSeasonIndices: force === true,
      startDate: dateValueFromParameter(source['dateFrom'] ?? source['DateFrom'], null),
      endDate: dateValueFromParameter(source['dateTo'] ?? source['DateTo'], null),
    };
  }
  if (kind === 'post-processing') {
    const productId = source['satelliteProductId'] ?? source['SatelliteProductId'];
    const priority = source['priority'] ?? source['Priority'];
    return {
      ...defaults,
      satelliteProductId: typeof productId === 'string' ? productId : '',
      priority: typeof priority === 'string' && priority.trim() ? priority : defaults.priority,
    };
  }
  if (kind === 'fields') {
    const schema = parameterFieldSchema(processKey) ?? [];
    return { ...defaults, parameterFields: parameterFieldValuesFromSource(schema, source) };
  }
  if (kind === 'json') {
    return { ...defaults, parametersJson: JSON.stringify(source, null, 2) };
  }

  const geometry = asRecord(source['geometry'] ?? source['Geometry']);
  const sourceType = firstString(geometry, 'sourceType', 'SourceType')
    ?? firstString(source, 'geometrySource', 'GeometrySource')
    ?? defaults.geometrySource;
  const providers = firstString(source, 'providers', 'Providers') ?? 'All';
  const startDate = dateValueFromParameter(source['startDate'] ?? source['StartDate'], defaults.startDate);
  const endDate = dateValueFromParameter(source['endDate'] ?? source['EndDate'], defaults.endDate);
  const name = firstString(source, 'name', 'Name');
  const priority = firstString(source, 'priority', 'Priority');
  const countryName = firstString(geometry, 'countryName', 'CountryName')
    ?? firstString(source, 'countryName', 'CountryName');
  const geoJson = firstString(geometry, 'geoJson', 'GeoJson');
  const geoJsonFileId = firstString(geometry, 'geoJsonFileId', 'GeoJsonFileId');
  const regionIds = stringList(geometry?.['regionIds'] ?? geometry?.['RegionIds']
    ?? source['regionIds'] ?? source['RegionIds']);
  const cloudCover = source['maximumCloudCover'] ?? source['MaximumCloudCover'];
  return {
    ...defaults,
    name: name ?? defaults.name,
    geometrySource: sourceType,
    regionIds,
    countryName: countryName ?? '',
    geoJson: geoJson ?? '',
    geoJsonFileId: geoJsonFileId ?? '',
    startDate,
    endDate,
    sentinel: providers === 'All' || providers === 'Sentinel2',
    landsat: providers === 'All' || providers === 'Landsat',
    priority: priority ?? defaults.priority,
    maximumCloudCover: typeof cloudCover === 'number' && Number.isFinite(cloudCover) ? cloudCover : null,
  };
}

export function assignmentStartFormFromJson(
  processKey: string,
  json?: string | null,
): AssignmentStartFormValue {
  if (!json || !json.trim()) {
    return defaultAssignmentStartForm(processKey);
  }
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return defaultAssignmentStartForm(processKey);
    }
    return assignmentStartFormFromParameters(processKey, parsed as Record<string, unknown>);
  } catch {
    return defaultAssignmentStartForm(processKey);
  }
}

export function canStartAssignment(processKey: string, value: AssignmentStartFormValue): boolean {
  if (!processKey) {
    return false;
  }
  const kind = assignmentStartKind(processKey);
  if (kind === 'post-processing') {
    return !!value.satelliteProductId.trim();
  }
  if (kind === 'farmer-reports') {
    return normalizeFarmerReportsMax(value.maxReportsPerRun) !== null;
  }
  if (kind === 'bid-templates') {
    return !!resolveAssignmentStartDate(value.startDate)
      && !!resolveAssignmentStartDate(value.endDate);
  }
  if (kind === 'catch-up') {
    return normalizeCatchUpTake(value.catchUpTake) !== null;
  }
  if (kind === 'probe') {
    return normalizeProbeFailEvery(value.failEvery) !== null;
  }
  if (kind === 'season-indices') {
    return seasonIndicesFormError(processKey, value) === null;
  }
  if (kind === 'fields') {
    return fieldsFormError(processKey, value) === null;
  }
  if (kind === 'json') {
    return !!value.parametersJson.trim();
  }
  return !!value.name.trim()
    && !!resolveAssignmentStartDate(value.startDate)
    && !!resolveAssignmentStartDate(value.endDate)
    && (value.sentinel || value.landsat)
    && (value.geometrySource !== 'GeoJson' || !!value.geoJson.trim());
}

export function buildAssignmentStartParameters(
  processKey: string,
  value: AssignmentStartFormValue,
): AssignmentStartBuildResult {
  const kind = assignmentStartKind(processKey);
  if (kind === 'post-processing') {
    return {
      ok: true,
      parameters: {
        satelliteProductId: value.satelliteProductId.trim(),
        priority: value.priority,
      },
    };
  }
  if (kind === 'farmer-reports') {
    const maxReportsPerRun = normalizeFarmerReportsMax(value.maxReportsPerRun);
    if (maxReportsPerRun === null) {
      return { ok: false, error: 'Max reports per run must be between 1 and 100' };
    }
    return {
      ok: true,
      parameters: buildFarmerReportsParameters(maxReportsPerRun, !!value.alsoPdf),
    };
  }
  if (kind === 'bid-templates') {
    const fromUtc = resolveAssignmentStartDate(value.startDate);
    const toUtc = resolveAssignmentStartDate(value.endDate);
    if (!fromUtc || !toUtc) {
      return { ok: false, error: 'Start date must be on or before the end date' };
    }
    if (!isSubscriptionDateToken(fromUtc) && !isSubscriptionDateToken(toUtc) && fromUtc > toUtc) {
      return { ok: false, error: 'Start date must be on or before the end date' };
    }
    const dateError = validateSubscriptionDatePlaceholders({ fromUtc, toUtc });
    if (dateError) {
      return { ok: false, error: dateError };
    }
    return {
      ok: true,
      parameters: buildBidTemplatesParameters(fromUtc, toUtc, value.locale, value.bucket),
    };
  }
  if (kind === 'catch-up') {
    const take = normalizeCatchUpTake(value.catchUpTake);
    if (take === null) {
      return { ok: false, error: 'Products per catch-up run must be between 1 and 500' };
    }
    return { ok: true, parameters: buildCatchUpParameters(take) };
  }
  if (kind === 'probe') {
    const failEvery = normalizeProbeFailEvery(value.failEvery);
    if (failEvery === null) {
      return { ok: false, error: 'failEvery must be an integer ≥ 1' };
    }
    return { ok: true, parameters: buildProbeParameters(failEvery) };
  }
  if (kind === 'season-indices') {
    const error = seasonIndicesFormError(processKey, value);
    if (error) {
      return { ok: false, error };
    }
    return { ok: true, parameters: buildSeasonIndicesParameters(processKey, value) };
  }
  if (kind === 'fields') {
    const error = fieldsFormError(processKey, value);
    if (error) {
      return { ok: false, error };
    }
    return { ok: true, parameters: buildFieldParameters(processKey, value) };
  }
  if (kind === 'json') {
    const parsed = parseProcessParametersJson(value.parametersJson);
    if (!parsed.ok) {
      return parsed;
    }
    const dateError = validateSubscriptionDatePlaceholders(parsed.parameters);
    if (dateError) {
      return { ok: false, error: dateError };
    }
    return parsed;
  }

  const startDate = resolveAssignmentStartDate(value.startDate);
  const endDate = resolveAssignmentStartDate(value.endDate);
  if (!startDate || !endDate) {
    return { ok: false, error: 'Start date must be on or before the end date' };
  }
  if (!isSubscriptionDateToken(startDate) && !isSubscriptionDateToken(endDate) && startDate > endDate) {
    return { ok: false, error: 'Start date must be on or before the end date' };
  }

  const providers = value.sentinel && value.landsat
    ? 'All'
    : value.sentinel
      ? 'Sentinel2'
      : value.landsat
        ? 'Landsat'
        : 'None';
  const geometry: Record<string, unknown> = { sourceType: value.geometrySource };
  if (value.geometrySource === 'Regions') {
    geometry['regionIds'] = value.regionIds.filter(Boolean);
  }
  if (value.geometrySource === 'CountryName') {
    geometry['countryName'] = value.countryName.trim();
  }
  if (value.geometrySource === 'GeoJson') {
    geometry['geoJson'] = value.geoJson;
  }
  if (value.geometrySource === 'GeoJsonFile') {
    geometry['geoJsonFileId'] = value.geoJsonFileId.trim();
  }

  const parameters: Record<string, unknown> = {
    name: value.name.trim(),
    geometry,
    providers,
    startDate,
    endDate,
    priority: value.priority,
  };
  if (processKey === 'satellite.find-products') {
    parameters['lookback'] = '2.00:00:00';
  }
  if (isSatelliteDownloadSelectionProcess(processKey) && value.maximumCloudCover !== null) {
    parameters['maximumCloudCover'] = value.maximumCloudCover;
  }
  return { ok: true, parameters };
}

export function assignmentStartApiError(error: unknown, fallback: string): string {
  if (!(error instanceof HttpErrorResponse)) {
    return fallback;
  }
  if (typeof error.error === 'string' && error.error.trim()) {
    return error.error;
  }

  const problem = error.error as {
    detail?: unknown;
    title?: unknown;
    errors?: Record<string, unknown>;
  } | null;
  const validationMessage = problem?.errors
    ? Object.values(problem.errors)
      .flatMap(value => Array.isArray(value) ? value : [value])
      .find((value): value is string => typeof value === 'string' && !!value.trim())
    : undefined;

  if (validationMessage) {
    return validationMessage;
  }
  if (typeof problem?.detail === 'string' && problem.detail.trim()) {
    return problem.detail;
  }
  if (typeof problem?.title === 'string' && problem.title.trim()) {
    return problem.title;
  }
  return fallback;
}

function defaultParametersJson(processKey: string): string {
  if (isFarmerReportsProcess(processKey)) {
    return JSON.stringify(buildFarmerReportsParameters(FARMER_REPORTS_DEFAULT_MAX, false), null, 2);
  }
  if (isBidTemplatesProcess(processKey)) {
    return JSON.stringify(buildBidTemplatesParameters(
      BID_TEMPLATES_DEFAULT_FROM,
      BID_TEMPLATES_DEFAULT_TO,
      BID_TEMPLATES_DEFAULT_LOCALE,
      BID_TEMPLATES_DEFAULT_BUCKET,
    ), null, 2);
  }
  if (isCatchUpPostProcessingProcess(processKey)) {
    return JSON.stringify(buildCatchUpParameters(CATCH_UP_DEFAULT_TAKE), null, 2);
  }
  if (isOrchestrationProbeProcess(processKey)) {
    return JSON.stringify(buildProbeParameters(PROBE_DEFAULT_FAIL_EVERY), null, 2);
  }
  if (isGeometryProcessKey(processKey)) {
    return `{
  "sourceType": "CountryBoundaries"
}`;
  }
  return satellitePipelineParametersTemplate(processKey) ?? '{}';
}

/** JSON template for satellite pipeline processes that take operator parameters. */
export function satellitePipelineParametersTemplate(processKey: string): string | null {
  if (processKey === 'satellite.find-landsat-products') {
    return `{
  "startDate": "{{today-5d}}",
  "endDate": "{{today}}"
}`;
  }
  if (processKey === 'satellite.load-sentinel-metadata' || processKey === 'satellite.convert-sentinel-tiles') {
    return `{
  "take": 100
}`;
  }
  if (processKey === 'satellite.build-shared-index-reports') {
    return `{
  "windowDays": 30
}`;
  }
  return null;
}

function isGuid(value: string): boolean {
  return GUID_PATTERN.test(value);
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function seasonIndicesFormError(processKey: string, value: AssignmentStartFormValue): string | null {
  const fieldId = value.fieldId.trim();
  if (isCalculateSeasonIndicesProcess(processKey) && fieldId && !isGuid(fieldId)) {
    return 'Field ID must be a GUID';
  }
  const jobId = value.polygonDetectionJobId.trim();
  if (isBackfillSeasonIndicesProcess(processKey) && jobId) {
    if (!isGuid(jobId)) {
      return 'Detection job must be a GUID';
    }
    if (jobId.toLowerCase() === EMPTY_GUID) {
      return 'Detection job must not be the empty GUID';
    }
  }
  const dateFrom = resolveAssignmentStartDate(value.startDate);
  const dateTo = resolveAssignmentStartDate(value.endDate);
  const hasFrom = !!dateFrom;
  const hasTo = !!dateTo;
  if (isBackfillSeasonIndicesProcess(processKey) && hasFrom !== hasTo) {
    return 'Date from and date to must both be set or both empty';
  }
  if (hasFrom && hasTo && dateFrom > dateTo) {
    return 'Start date must be on or before the end date';
  }
  return null;
}

function buildSeasonIndicesParameters(
  processKey: string,
  value: AssignmentStartFormValue,
): Record<string, unknown> {
  const parameters: Record<string, unknown> = {};
  const fieldId = value.fieldId.trim();
  if (isCalculateSeasonIndicesProcess(processKey) && fieldId) {
    parameters['fieldId'] = fieldId;
  }
  const dateFrom = resolveAssignmentStartDate(value.startDate);
  const dateTo = resolveAssignmentStartDate(value.endDate);
  if (dateFrom) {
    parameters['dateFrom'] = dateFrom;
  }
  if (dateTo) {
    parameters['dateTo'] = dateTo;
  }
  if (isBackfillSeasonIndicesProcess(processKey) && value.forceSeasonIndices) {
    parameters['force'] = true;
  }
  const jobId = value.polygonDetectionJobId.trim();
  if (isBackfillSeasonIndicesProcess(processKey) && jobId) {
    parameters['polygonDetectionJobId'] = jobId;
  }
  return parameters;
}

function fieldsFormError(processKey: string, value: AssignmentStartFormValue): string | null {
  const schema = parameterFieldSchema(processKey) ?? [];
  const fields = value.parameterFields ?? {};
  for (const field of schema) {
    if (field.required && !String(fields[field.name] ?? '').trim()) {
      return 'Required process parameter is empty';
    }
  }
  const lat = String(fields['Lat'] ?? '').trim();
  const lng = String(fields['Lng'] ?? '').trim();
  if ((lat && !lng) || (!lat && lng)) {
    return 'Latitude and longitude must both be set';
  }
  if (processKey.startsWith('weather.check-') && !String(fields['RegionIds'] ?? '').trim() && !(lat && lng)) {
    return 'A region or a coordinate point is required';
  }
  const polygon = schema.find(field => field.type === 'polygon');
  if (polygon && String(fields[polygon.name] ?? '').trim()) {
    try {
      JSON.parse(String(fields[polygon.name]));
    } catch {
      return 'Process parameters must be a valid JSON object';
    }
  }
  const dateFields = schema.filter(field => field.type === 'date');
  const filledDates = dateFields.filter(field => String(fields[field.name] ?? '').trim());
  if (dateFields.length > 1 && filledDates.length === 1) {
    return 'Date from and date to must both be set or both empty';
  }
  const dates = dateFields.map(field => resolveAssignmentStartDate(fields[field.name] || null));
  if (dates.length >= 2 && dates[0] && dates[1] && !isSubscriptionDateToken(dates[0]) && !isSubscriptionDateToken(dates[1]) && dates[0] > dates[1]) {
    return 'Start date must be on or before the end date';
  }
  return null;
}

function buildFieldParameters(processKey: string, value: AssignmentStartFormValue): Record<string, unknown> {
  const schema = parameterFieldSchema(processKey) ?? [];
  const fields = value.parameterFields ?? {};
  const parameters: Record<string, unknown> = {};
  for (const field of schema) {
    if (field.name === 'Lat' || field.name === 'Lng') {
      continue;
    }
    const raw = String(fields[field.name] ?? '').trim();
    if (field.type === 'checkbox') {
      parameters[field.name] = raw === 'true';
      continue;
    }
    if (!raw) {
      continue;
    }
    if (field.type === 'number') {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        parameters[field.name] = parsed;
      }
      continue;
    }
    if (field.type === 'date') {
      const resolved = resolveAssignmentStartDate(raw);
      if (resolved) {
        parameters[field.name] = resolved;
      }
      continue;
    }
    if (field.type === 'regions' || field.type === 'checks') {
      const items = raw.split(',').map(item => item.trim()).filter(Boolean);
      if (field.name === 'RegionId') {
        parameters[field.name] = items[0];
      } else if (items.length) {
        parameters[field.name] = items;
      }
      continue;
    }
    if (field.type === 'polygon') {
      parameters[field.name] = JSON.parse(raw);
      continue;
    }
    parameters[field.name] = raw;
  }
  const lat = String(fields['Lat'] ?? '').trim();
  const lng = String(fields['Lng'] ?? '').trim();
  if (lat && lng) {
    parameters['Points'] = [{ Lat: Number(lat), Lng: Number(lng) }];
  }
  return parameters;
}

function resolveAssignmentStartDate(value: Date | string | null): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string' && isSubscriptionDateToken(value)) {
    return value.trim();
  }
  return parseFlexibleDateToIso(value);
}

function dateFromIso(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function dateValueFromParameter(value: unknown, fallback: Date | string | null): Date | string | null {
  if (typeof value === 'string' && isSubscriptionDateToken(value)) {
    return value.trim();
  }
  if (typeof value !== 'string' && !(value instanceof Date)) {
    return fallback;
  }
  const iso = parseFlexibleDateToIso(value);
  return iso ? dateFromIso(iso) : fallback;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function firstString(source: Record<string, unknown> | null | undefined, ...names: string[]): string | undefined {
  if (!source) {
    return undefined;
  }
  for (const name of names) {
    const value = source[name];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && !!item.trim())
    : [];
}
